import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

// Layouts
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';

// Public Pages
import Home from './pages/Home';
import Stay from './pages/Stay';
import Rates from './pages/Rates';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import Location from './pages/Location';
import ScenicViews from './pages/ScenicViews';
import WarmWelcome from './pages/WarmWelcome';
import LocalFinds from './pages/LocalFinds';
import PahiyasSpirit from './pages/PahiyasSpirit';
import BookingConfirmation from './pages/BookingConfirmation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import NotFound from './pages/NotFound';

// Admin Pages
import AdminLogin from './pages/Admin/Login';
import AdminSetup from './pages/Admin/Setup';
import AdminBootstrap from './pages/Admin/Bootstrap';
import AdminDashboard from './pages/Admin/Dashboard';
import AccessManagement from './pages/Admin/AccessManagement';
import DeveloperPortal from './pages/Admin/DeveloperPortal';
import IcalSync from './pages/Admin/IcalSync';
import ApiDocs from './pages/Admin/ApiDocs';

// Auth Guard
const ProtectedRoute = ({ children, allowedRoles = [], requiredPermission = null }) => {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') setUser(data.user);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-brand-cream/20">
    <div className="w-12 h-12 border-4 border-brand-olive border-t-transparent rounded-full animate-spin"></div>
  </div>;

  if (!user) return <Navigate to="/admin/login" replace />;

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect developers to their portal if they try to access general admin
    if (user.role === 'developer') return <Navigate to="/developer" replace />;
    // Redirect business admins to their dashboard if they try to access developer portal
    if (user.role === 'business_admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/admin" replace />;
  }

  if (requiredPermission && user.role === 'temporary_admin') {
    const perms = typeof user.permissions === 'string' ? JSON.parse(user.permissions || "[]") : (user.permissions || []);
    if (!perms.includes(requiredPermission)) return <Navigate to="/admin" replace />;
  }

  return children;
};

// Compatibility Redirect for Hash-based Routes
const HashRedirect = () => {
  React.useEffect(() => {
    if (window.location.hash.startsWith('#/')) {
      const path = window.location.hash.slice(2);
      window.history.replaceState(null, '', `/${path}`);
    }
  }, []);
  return null;
};

function App() {
  return (
    <Router>
      <HashRedirect />
      <AnimatePresence mode="wait">
        <Routes>
          {/* Public Routes nested inside MainLayout */}
          <Route element={<MainLayout />}>
            <Route index element={<Home />} />
            <Route path="stay" element={<Stay />} />
            <Route path="rates" element={<Rates />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="contact" element={<Contact />} />
            <Route path="location" element={<Location />} />
            <Route path="scenic-views" element={<ScenicViews />} />
            <Route path="warm-welcome" element={<WarmWelcome />} />
            <Route path="local-finds" element={<LocalFinds />} />
            <Route path="pahiyas-spirit" element={<PahiyasSpirit />} />
            <Route path="booking-confirmation" element={<BookingConfirmation />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-conditions" element={<TermsConditions />} />
          </Route>

          {/* Admin Authentication & Setup */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/bootstrap" element={<AdminBootstrap />} />

          {/* Admin Routes nested inside AdminLayout */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={
              <ProtectedRoute allowedRoles={['super_admin', 'temporary_admin', 'business_admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="access-management" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AccessManagement />
              </ProtectedRoute>
            } />
            <Route path="ical-sync" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <IcalSync />
              </ProtectedRoute>
            } />
          </Route>

          {/* Developer Portal */}
          <Route path="/developer" element={<AdminLayout />}>
            <Route index element={
              <ProtectedRoute allowedRoles={['developer']}>
                <DeveloperPortal />
              </ProtectedRoute>
            } />
            <Route path="api-docs" element={
              <ProtectedRoute allowedRoles={['developer']}>
                <ApiDocs />
              </ProtectedRoute>
            } />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </Router>
  );
}

export default App;